import { z } from "zod";
// Whether or not the property should apply, given the state of the postfix
const PropertyPrefix = z.enum([
    "default",
    "freeTrial"
]);
const PropertyHidden = z.object({
    type: z.literal("hidden"),
    hidden: z.boolean()
});
const PropertyVideo = z.object({
    type: z.literal("video"),
    coverURL: z.string().nullable().optional(),
    videoURLs: z.array(z.object({
        url: z.string()
    }))
});
const PropertySrc = z.object({
    type: z.literal("src"),
    src: z.string(),
    srcSet: z.array(z.string()).optional()
});
const Style = z.object({
    property: z.string(),
    value: z.string()
});
const Styles = z.array(Style);
const PropertyStyles = z.object({
    type: z.literal("styles"),
    styles: Styles
});
const PropertyBackgroundImage = z.object({
    type: z.literal("backgroundImage"),
    backgroundImage: z.string().nullable().optional(),
    backgroundSize: z.string().nullable().optional(),
    backgroundRepeat: z.string().nullable().optional(),
    backgroundPosition: z.string().nullable().optional(),
    backgroundAttachment: z.string().nullable().optional()
});
const ClickBehaviorDoNothing = z.object({
    type: z.literal("do-nothing")
});
const ClickBehaviorOpenURL = z.object({
    type: z.literal("open-url"),
    url: z.string()
});
const ClickBehaviorOpenExternalURL = z.object({
    type: z.literal("open-url-external"),
    url: z.string()
});
const ClickBehaviorOpenDeepLink = z.object({
    type: z.literal("open-deep-link"),
    link: z.string()
});
const ClickBehaviorClose = z.object({
    type: z.literal("close")
});
const ClickBehaviorRestore = z.object({
    type: z.literal("restore")
});
const ClickBehaviorPurchase = z.object({
    type: z.literal("purchase"),
    product: z.union([
        z.literal("primary"),
        z.literal("secondary"),
        z.literal("tertiary")
    ])
});
const ClickBehaviorCustom = z.object({
    type: z.literal("custom"),
    data: z.string()
});
export const ClickBehavior = z.discriminatedUnion("type", [
    ClickBehaviorDoNothing,
    ClickBehaviorOpenURL,
    ClickBehaviorOpenExternalURL,
    ClickBehaviorOpenDeepLink,
    ClickBehaviorClose,
    ClickBehaviorRestore,
    ClickBehaviorPurchase,
    ClickBehaviorCustom
]);
const PropertyClickBehavior = z.object({
    type: z.literal("click-behavior"),
    clickBehavior: ClickBehavior
});
/**
 * TODO make the purchase button validation dynamic to the
 * number of products a client might have. We currently set
 * the product limit to three but in the future we should
 * allow an unlimited number of products to be used in a paywall.
 *
 * Temporary hack to get form validation to work when setting
 * the click behavior on an element in the paywall editor.
 */ const ClickBehaviorPurchasePrimary = z.object({
    type: z.literal("purchase-Primary"),
    product: z.string()
});
const ClickBehaviorPurchaseSecondary = z.object({
    type: z.literal("purchase-Secondary"),
    product: z.string()
});
const ClickBehaviorPurchaseTertiary = z.object({
    type: z.literal("purchase-Tertiary"),
    product: z.string()
});
export const ClickBehaviorEditorSchema = z.discriminatedUnion("type", [
    ClickBehaviorDoNothing,
    ClickBehaviorOpenURL,
    ClickBehaviorOpenExternalURL,
    ClickBehaviorOpenDeepLink,
    ClickBehaviorClose,
    ClickBehaviorRestore,
    ClickBehaviorCustom,
    ClickBehaviorPurchasePrimary,
    ClickBehaviorPurchaseSecondary,
    ClickBehaviorPurchaseTertiary
]);
const PropertyClickBehaviorEditorSchema = z.object({
    type: z.literal("click-behavior"),
    clickBehavior: ClickBehaviorEditorSchema
});
export const ClickBehaviorAll = z.discriminatedUnion("type", [
    ClickBehaviorDoNothing,
    ClickBehaviorOpenURL,
    ClickBehaviorOpenExternalURL,
    ClickBehaviorOpenDeepLink,
    ClickBehaviorClose,
    ClickBehaviorRestore,
    ClickBehaviorCustom,
    ClickBehaviorPurchase,
    ClickBehaviorPurchasePrimary,
    ClickBehaviorPurchaseSecondary,
    ClickBehaviorPurchaseTertiary
]);
const PropertyClickBehaviorAll = z.object({
    type: z.literal("click-behavior"),
    clickBehavior: ClickBehaviorAll
});
export const SubPropertyValue = z.union([
    PropertyHidden,
    PropertySrc,
    PropertyStyles,
    PropertyVideo,
    PropertyClickBehavior,
    PropertyBackgroundImage
]);
export const PropertyAssets = z.union([
    PropertySrc,
    PropertyVideo
]);
export const SubProperty = z.object({
    prefix: PropertyPrefix,
    property: SubPropertyValue
});
/**
 *
 * {
 *    "key": "title",
 *    "value": "Free Trial",
 *    "tagName": "h1",
 *    "properties": [
 *      {
 *        "prefix": "default",
 *        "value": {
 *         "type": "src",
 *         "src": "https://example.com/image.png"
 *       }
 *      }
 *     ]
 * }
 *
 *
 */ export const properties = z.array(SubProperty);
export const SubSchema = z.object({
    key: z.string(),
    value: z.string(),
    freeTrialValue: z.string().optional(),
    // If this is set, we don't template the innerHTML
    skipInnerHTML: z.boolean().optional(),
    // If this is set, we don't every touch this element
    // ignoreDOM: z.boolean().optional(),
    // The _actual_ tag name of the element
    tagName: z.string().optional(),
    // What type the tag plays in the paywall,
    // used for controlling what type of editor
    // we setup
    subType: z.enum([
        "img",
        "video",
        "var"
    ]).optional(),
    properties: properties.optional(),
    initialStyles: z.object({}).passthrough().optional(),
    // Used to break memoization in the editor
    revision: z.number().optional()
});
export const SubsSchema = z.array(SubSchema);
// This is the schema of substitutionso once paywall-js has rendered them,
// they don't have any notion of free trial or default.
export const RenderedSubsSchema = z.object({
    key: z.string(),
    // Null implies that that value should be removed from the DOM
    // undefined implies that the value should be left alone
    value: z.string().nullable().optional(),
    skipInnerHTML: z.boolean().optional(),
    properties: z.array(properties)
});
